// src/components/LeafletMapNearbyUsers.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../context/ChatContext';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { publicGet } from '../lib/apiPublic';
import { http } from '../lib/axios';
import MiniArtistCard from './MiniArtistCard';
import MiniHostCard from './MiniHostCard';
import {
  DEFAULT_DISCOVERY_POSITION,
  readStoredDiscoveryPosition,
  toLeafletPosition,
  writeStoredDiscoveryPosition,
} from '../utils/discoveryLocation';

const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const blueIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',   // hosts
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const greenIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png', // artists
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const redIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',   // buyer
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});

function MapEventHandler({ setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
      console.log('e.latlng.lat', e.latlng.lat);
      console.log('e.latlng.lng', e.latlng.lng);
    }
  });
  return null;
}

function MapCenterUpdater({ center }) {
  const map = useMap();

  useEffect(() => {
    if (!center) return;
    map.setView(center);
  }, [map, center]);

  return null;
}

export default function LeafletMapNearbyUsers({
  roleFilter,
  distance,
  buyer,
  onStartChat,
  setNearbyArtistArtworks,
  setNearbyHostedArtworks,
  position: controlledPosition,
  setPosition: controlledSetPosition,
  fallbackToDefaultOnEmpty = false,
}) {
  const { launchChat } = useChat();
  const [internalPosition, setInternalPosition] = useState(null);
  const [users, setUsers] = useState([]);
  const prevPositionRef = useRef(null);
  const prevDistanceRef = useRef(null);
  const didFallbackRef = useRef(false);
  const navigate = useNavigate();
  const buyerProfile = useMemo(() => buyer?.[0] || {}, [buyer]);
  const roleFilterKey = useMemo(() => JSON.stringify(roleFilter || []), [roleFilter]);
  const isAuthenticated = !!localStorage.getItem('access_token');
  const startChat = onStartChat || launchChat;
  const position = controlledPosition || internalPosition;
  const setPosition = controlledSetPosition || setInternalPosition;

  useEffect(() => {
    if (!position && !buyerProfile?.location) {
      const storedPosition = toLeafletPosition(readStoredDiscoveryPosition());
      if (storedPosition) {
        setPosition(storedPosition);
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setPosition([latitude, longitude]);
          writeStoredDiscoveryPosition({ lat: latitude, lng: longitude });
        },
        (err) => {
          console.error("Location error:", err);
          setPosition(storedPosition || toLeafletPosition(DEFAULT_DISCOVERY_POSITION)); // fallback: LA
        },
        {
          enableHighAccuracy: false,
          maximumAge: 5 * 60 * 1000,
          timeout: 4000,
        }
      );
    }
  }, [buyerProfile, position, setPosition]);

  useEffect(() => {
    if (!position) return;

    if (Array.isArray(position) && position.length === 2) {
      writeStoredDiscoveryPosition({ lat: position[0], lng: position[1] });
    }
  }, [position]);

  useEffect(() => {
    if (position) {
      const [lat, lng] = position;
      const prevPos = prevPositionRef.current;
      const prevDist = prevDistanceRef.current;
      const shouldFetch = !prevPos || prevPos[0] !== lat || prevPos[1] !== lng || prevDist !== distance;

      if (shouldFetch) {
        prevPositionRef.current = [lat, lng];
        prevDistanceRef.current = distance;

        console.log('new location:', position); 

        const fetchNearbyUsers = async () => {
          try {
            const res = await publicGet(`/api/accounts/nearby_users/?role=${roleFilter.join(',')}&distance=${distance}&lat=${lat}&lng=${lng}`);
            console.log('Fetched users at new location:', res.data);
            setUsers(res.data);
            return Array.isArray(res.data) ? res.data : [];
          } catch (err) {
            console.error('Failed to fetch nearby users', err);
            setUsers([]);
            return [];
          }
        };

        const fetchNearbyArtworks = async (type) => {
          const endpoint = type === "artists" ? "nearby_artists" : "nearby_hosted";
          try {
            const { data } = await http.get(`/artworks/${endpoint}/`, {
              params: { distance, lat, lng, view: "card" },
              ignore401: true,
            });

            console.log(`Fetched ${type} artworks at ${lat},${lng}:`, data);

            if (type === "artists" && setNearbyArtistArtworks) {
              setNearbyArtistArtworks(data);
            }
            if (type === "hosts" && setNearbyHostedArtworks) {
              setNearbyHostedArtworks(data);
            }
            return Array.isArray(data) ? data : [];
          } catch (error) {
            console.error(`Failed to fetch ${type} artworks`, error);
            return [];
          }
        };

        // Wrap the await call in an async IIFE
        (async () => {
          const [nearbyUsersResult, artistResult, hostResult] = await Promise.all([
            fetchNearbyUsers(),
            fetchNearbyArtworks("artists"),
            fetchNearbyArtworks("hosts"),
          ]);

          const noUsers = nearbyUsersResult.length === 0;
          const noArtistArtworks = artistResult.length === 0;
          const noHostArtworks = hostResult.length === 0;
          const alreadyAtDefault =
            lat === DEFAULT_DISCOVERY_POSITION.lat && lng === DEFAULT_DISCOVERY_POSITION.lng;

          if (
            fallbackToDefaultOnEmpty &&
            !didFallbackRef.current &&
            noUsers &&
            noArtistArtworks &&
            noHostArtworks &&
            !alreadyAtDefault
          ) {
            didFallbackRef.current = true;
            console.info("No nearby discovery results for current location. Falling back to Los Angeles.");
            setPosition(toLeafletPosition(DEFAULT_DISCOVERY_POSITION));
          }
        })();
      }
    }
  }, [position, distance, roleFilter, roleFilterKey, setNearbyArtistArtworks, setNearbyHostedArtworks, fallbackToDefaultOnEmpty, setPosition]);

  useEffect(() => {
    if (!position && buyerProfile?.location?.latitude && buyerProfile?.location?.longitude) {
      setPosition([buyerProfile.location.latitude, buyerProfile.location.longitude]);
    }
  }, [buyerProfile, position, setPosition]);

  if (!position) return <p>Loading map...</p>;

  return (
    <MapContainer center={position} zoom={10} style={{ height: '400px', width: '100%' }}>
      <MapCenterUpdater center={position} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Circle center={position} radius={distance * 1000} pathOptions={{ color: 'blue' }} />
      <MapEventHandler setPosition={setPosition} />
      <Marker position={position} icon={redIcon}>
        <Popup>You are here</Popup>
      </Marker>
      {users.map(user => {
        const lat = user?.location?.latitude;
        const lng = user?.location?.longitude;

        if (typeof lat !== 'number' || typeof lng !== 'number') return null;
        const role = user.role;
        const profilePath = role === 'artist' ? `/artists/${user.id}` : role === 'host' ? `/hosts/${user.id}` : '#';

        return (
          <Marker
            key={user.user_id}
            position={[lat, lng]}
            icon={role === 'artist' ? greenIcon : role === 'host' ? blueIcon : DefaultIcon}
          >
            <Popup >
              {/* Compact card preview */}
              {role === 'artist' ? (
                <MiniArtistCard key={`artist-${user.id}`} id={user.id} />
              ) : role === 'host' ? (
                <MiniHostCard key={`host-${user.id}`} id={user.id} />
              ) : (
                <div>
                  <strong>{user.first_name} {user.last_name}</strong><br />
                  {role}
                </div>
              )}
              
              {/* Actions */}
              <Stack direction="row" spacing={1} mt={1}>
                <Button size="small" variant="outlined" onClick={() => navigate(profilePath)}>
                  View Profile
                </Button>
                {isAuthenticated && startChat && (
                  <Button size="small" variant="contained" onClick={() => startChat(user)}>
                    Message
                  </Button>
                )}
              </Stack>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}
